/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { ArrowUp, StopFilledAlt } from '@carbon/icons-react';
import { useRef } from 'react';

import * as Styled from './TextAreaAndSubmit.styled';

export const TextAreaAndSubmit = ({
  placeholderText,
  isProcessing,
  text,
  onChangeText,
  nextFocusRef,
  onCancel,
  onSubmit,
  heightPixels = 68,
  paddingPixels = 10
}) => {
  const textboxRef = useRef(null);

  const handleSubmitButtonClick = async () => {
    if (isProcessing) {
      onCancel();
    } else {
      onSubmit(null);
    }
  };

  const onTextareaKeydown = (event) => {
    const { code, shiftKey } = event;

    if (code !== 'Enter') {
      return;
    }

    event.preventDefault();

    if (shiftKey) {
      onChangeText((value) => `${value}\n`);
    } else if (!isProcessing) {
      handleSubmitButtonClick();
    }
  };

  const onSubmitKeydown = (event) => {
    const { code, shiftKey } = event;

    if (code !== 'Tab') {
      return;
    }

    event.preventDefault();

    if (shiftKey) {
      textboxRef?.current?.focus();
    } else {
      nextFocusRef?.current?.focus();
    }
  };

  const renderIcon = () => {
    return isProcessing ? <StopFilledAlt /> : <ArrowUp width="16px" height="16px" />;
  };

  const onChange = (event) => {
    onChangeText(event.target.value);
  };

  return (
    <Styled.ChatboxForm>
      <Styled.StyledTextArea
        id="copilot-chatbox-textarea"
        aria-label="copilot-chatbox-textarea"
        value={text}
        $height={heightPixels}
        $padding={paddingPixels}
        placeholder={placeholderText}
        onChange={onChange}
        onKeyDown={onTextareaKeydown}
        ref={textboxRef}
      />
      <Styled.ButtonWrapper>
        <Styled.ChatboxSend
          hasIconOnly
          aria-label={isProcessing ? 'Cancel' : 'Send'}
          iconDescription={isProcessing ? 'Cancel' : 'Send'}
          size={'md'}
          kind={'secondary'}
          renderIcon={renderIcon}
          onKeyDown={onSubmitKeydown}
          onClick={handleSubmitButtonClick}
        />
      </Styled.ButtonWrapper>
    </Styled.ChatboxForm>
  );
};
